import React, { useState, useEffect, useCallback, useRef } from "react";
import useAppLevelAuth from "../hooks/useAppLevelAuth";
import getRole from "../Auth/GetRole";
import authHeader from "../Auth/authHeader";
import { ApiLink } from "../APILink/ApiLink";
import "./LayoutNavbar.scss";
import NewHeader from "./NewHeader";
import LegacySidebar from "./NewSidebar";
import SmartSidebar from "./SmartSidebar";

// Quick rollback switch:
// true  => new DB-driven sidebar
// false => legacy sidebar
const USE_DB_SIDEBAR = false; // vendose true per me perdor sidebarin e ri
const THEME_STORAGE_KEY = "accountree-theme";
const THEME_MODES = ["light", "dark-green", "dark-black"];
// Temporary switch requested by user:
// false => hide theme control and keep UI in light mode
// true  => show theme control with all theme options
const SHOW_THEME_SWITCHER = false; //vendose true per me perdor dark mode

const ActiveSidebar = USE_DB_SIDEBAR ? SmartSidebar : LegacySidebar;

const normalizeThemeMode = (value) => {
  return THEME_MODES.includes(value) ? value : "light";
};

const getInitialTheme = () => {
  if (typeof window === "undefined") {
    return "light";
  }

  const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  if (storedTheme === "dark") {
    return "dark-green";
  }

  if (THEME_MODES.includes(storedTheme)) {
    return storedTheme;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark-green"
    : "light";
};

export default function LayoutNavbar({ children }) {
  const { isLoggedIn } = useAppLevelAuth();

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [userRole, setUserRole] = useState("user");
  const [menuSearchTerm, setMenuSearchTerm] = useState("");
  const [hasInitializedLayout, setHasInitializedLayout] = useState(false);
  const [themeMode, setThemeMode] = useState(getInitialTheme);
  const hasLoadedThemeFromDb = useRef(false);
  const hasFetchedThemeFromDbSuccessfully = useRef(false);
  const lastThemeFromDb = useRef(null);
  const userInitiatedThemeChange = useRef(false);

  useEffect(() => {
    // Keep light UI while switcher is hidden, but preserve full theme logic in code.
    if (!SHOW_THEME_SWITCHER && themeMode !== "light") {
      setThemeMode("light");
    }
  }, [themeMode]);

  useEffect(() => {
    if (isLoggedIn) {
      const headers = authHeader();
      getRole(headers).then((role) => {
        setUserRole(role || "user");
      });
    } else {
      setUserRole("user");
    }
  }, [isLoggedIn]);

  // ✅ Responsive behavior (IDENTIK)
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile((prevIsMobile) => {
        // React only when crossing mobile/desktop breakpoint.
        if (prevIsMobile !== mobile) {
          setIsSidebarOpen(!mobile);
        }
        return mobile;
      });

      if (!hasInitializedLayout) {
        setIsSidebarOpen(!mobile);
        setHasInitializedLayout(true);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Close sidebar on route change (IDENTIK)
  useEffect(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const isDarkTheme = themeMode !== "light";
    const themeVariant = themeMode === "dark-black" ? "black" : "green";

    document.documentElement.setAttribute("data-theme", isDarkTheme ? "dark" : "light");
    document.documentElement.setAttribute("data-theme-variant", isDarkTheme ? themeVariant : "light");
    document.documentElement.style.colorScheme = isDarkTheme ? "dark" : "light";
    localStorage.setItem(THEME_STORAGE_KEY, themeMode);
  }, [themeMode]);

  useEffect(() => {
    if (!isLoggedIn) {
      hasLoadedThemeFromDb.current = false;
      hasFetchedThemeFromDbSuccessfully.current = false;
      lastThemeFromDb.current = null;
      userInitiatedThemeChange.current = false;
      return;
    }

    let isCancelled = false;

    const loadThemeFromDb = async () => {
      try {
        const response = await fetch(`${ApiLink.API_URL}UserPreferences/theme`, {
          method: "GET",
          headers: {
            ...authHeader(),
          },
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        const themeFromDbRaw = data?.themeMode ?? data?.ThemeMode;
        if (typeof themeFromDbRaw !== "string") {
          return;
        }

        const nextTheme = normalizeThemeMode(themeFromDbRaw);

        if (!isCancelled) {
          hasFetchedThemeFromDbSuccessfully.current = true;
          lastThemeFromDb.current = nextTheme;
          setThemeMode((prevTheme) => (prevTheme === nextTheme ? prevTheme : nextTheme));
        }
      } catch {
        // Fallback remains localStorage/local state.
      } finally {
        if (!isCancelled) {
          hasLoadedThemeFromDb.current = true;
        }
      }
    };

    loadThemeFromDb();

    return () => {
      isCancelled = true;
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !hasLoadedThemeFromDb.current) {
      return;
    }

    if (!hasFetchedThemeFromDbSuccessfully.current) {
      return;
    }

    if (!userInitiatedThemeChange.current) {
      return;
    }

    if (lastThemeFromDb.current === themeMode) {
      return;
    }

    const saveThemeToDb = async () => {
      try {
        const response = await fetch(`${ApiLink.API_URL}UserPreferences/theme`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeader(),
          },
          body: JSON.stringify({ themeMode }),
        });

        if (response.ok) {
          lastThemeFromDb.current = themeMode;
          userInitiatedThemeChange.current = false;
        }
      } catch {
        // Silent fail: theme remains applied locally.
      }
    };

    saveThemeToDb();
  }, [themeMode, isLoggedIn]);

  const toggleSidebar = useCallback(() => setIsSidebarOpen((prev) => !prev), []);
  const setTheme = useCallback((nextTheme) => {
    if (THEME_MODES.includes(nextTheme)) {
      userInitiatedThemeChange.current = true;
      setThemeMode(nextTheme);
    }
  }, []);

  if (!isLoggedIn) {
    return <main className="layout-main-full">{children}</main>;
  }

  // ✅ Margin logic IDENTIK
  const mainClass = `layout-main ${
    isMobile ? "ml-0" : isSidebarOpen ? "ml-260" : "ml-80"
  }`;

  return (
    <div className="layout-container">
      <NewHeader
        onToggleSidebar={toggleSidebar}
        searchTerm={menuSearchTerm}
        onSearchChange={setMenuSearchTerm}
        themeMode={themeMode}
        onSetTheme={setTheme}
        showThemeSwitcher={SHOW_THEME_SWITCHER}
      />

      <ActiveSidebar
        isOpen={isSidebarOpen}
        isMobile={isMobile}
        onCloseMobile={() => setIsSidebarOpen(false)}
        onExpand={() => setIsSidebarOpen(true)}
        userRole={userRole}
        searchTerm={menuSearchTerm}
      />

      <main className={mainClass}>
        <div className="layout-content">{children}</div>
      </main>
    </div>
  );
}
// "use client";

// import React, { useState, useEffect } from "react";
// import { usePathname } from "next/navigation";
// import useAppLevelAuth from "@/hooks/useAppLevelAuth";
// import './LayoutNavbar.scss';
// import NewHeader from "./NewHeader";
// import NewSidebar from "./NewSidebar";

// export default function LayoutNavbar({ children }) {
//   const { isLoggedIn } = useAppLevelAuth();
//   const [isSidebarOpen, setIsSidebarOpen] = useState(true);
//   const [isMobile, setIsMobile] = useState(false);
//   const [userRole, setUserRole] = useState("user");
//   const pathname = usePathname();

//   useEffect(() => {
//     if (isLoggedIn) {
//       import("@/entities/User").then(({ User }) => {
//         User.me().then((user) => {
//           if (user) setUserRole(user.role || "user");
//         });
//       });
//     }
//   }, [isLoggedIn]);

//   // Responsive behavior (IDENTIK)
//   useEffect(() => {
//     const handleResize = () => {
//       const mobile = window.innerWidth < 1024;
//       setIsMobile(mobile);

//       if (mobile) {
//         setIsSidebarOpen(false);
//       } else {
//         setIsSidebarOpen(true);
//       }
//     };

//     handleResize();

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   // Close sidebar on route change (IDENTIK)
//   useEffect(() => {
//     if (isMobile) {
//       setIsSidebarOpen(false);
//     }
//   }, [pathname, isMobile]);

//   const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

//   if (!isLoggedIn) {
//     return <main className="layout-main-full">{children}</main>;
//   }

//   // Margin logic IDENTIK
//   const mainClass = `layout-main ${
//     isMobile
//       ? "ml-0"
//       : isSidebarOpen
//       ? "ml-260"
//       : "ml-80"
//   }`;

//   return (
//     <div className="layout-container">
//       <NewHeader onToggleSidebar={toggleSidebar} />

//       <NewSidebar
//         isOpen={isSidebarOpen}
//         isMobile={isMobile}
//         onCloseMobile={() => setIsSidebarOpen(false)}
//         userRole={userRole}
//       />

//       <main className={mainClass}>
//         <div className="layout-content">{children}</div>
//       </main>
//     </div>
//   );
// }